import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Mail, Lock, ArrowRight, Loader2, ArrowLeft } from 'lucide-react';
import './Auth.css';

export default function Auth() {
    const [mode, setMode] = useState('login'); // 'login', 'signup', 'forgot'
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');

    const { signIn, signUp, resetPassword } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setLoading(true);

        try {
            if (mode === 'login') {
                await signIn(email, password);
                const pendingPriceId = sessionStorage.getItem('pending_checkout_price_id');
                if (pendingPriceId) {
                    navigate('/#pricing');
                } else {
                    navigate('/dashboard');
                }
            } else if (mode === 'signup') {
                await signUp(email, password);
                const pendingPriceId = sessionStorage.getItem('pending_checkout_price_id');
                if (pendingPriceId) {
                    setMessage('Account created successfully! Check your email for confirmation, then sign in to complete upgrade.');
                } else {
                    setMessage('Check your email for a confirmation link!');
                }
            } else if (mode === 'forgot') {
                await resetPassword(email);
                setMessage('Password reset link sent! Check your email.');
            }
        } catch (err) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const getTitle = () => {
        if (mode === 'forgot') return 'Reset Password';
        if (mode === 'signup') return 'Create your account';
        return 'Welcome back';
    };

    const getSubtitle = () => {
        if (mode === 'forgot') return 'Enter your email to receive a reset link';
        if (mode === 'signup') return 'Start your free trial today';
        return 'Sign in to access your dashboard';
    };

    return (
        <div className="auth">
            <div className="auth__container">
                <div className="auth__header">
                    <a href="/" className="auth__brand">REVTRONE</a>
                    <h1 className="auth__title">{getTitle()}</h1>
                    <p className="auth__subtitle">{getSubtitle()}</p>
                </div>

                {mode !== 'forgot' && (
                    <div className="auth__tabs">
                        <button
                            className={`auth__tab ${mode === 'login' ? 'auth__tab--active' : ''}`}
                            onClick={() => { setMode('login'); setError(''); setMessage(''); }}
                            type="button"
                        >
                            Sign In
                        </button>
                        <button
                            className={`auth__tab ${mode === 'signup' ? 'auth__tab--active' : ''}`}
                            onClick={() => { setMode('signup'); setError(''); setMessage(''); }}
                            type="button"
                        >
                            Sign Up
                        </button>
                    </div>
                )}

                {mode === 'forgot' && (
                    <button
                        className="auth__backBtn"
                        onClick={() => { setMode('login'); setError(''); setMessage(''); }}
                        type="button"
                    >
                        <ArrowLeft size={16} />
                        Back to Sign In
                    </button>
                )}

                <form className="auth__form" onSubmit={handleSubmit}>
                    {error && <div className="auth__error">{error}</div>}
                    {message && <div className="auth__message">{message}</div>}

                    <div className="auth__field">
                        <label className="auth__label" htmlFor="email">Email</label>
                        <div className="auth__inputWrapper">
                            <Mail className="auth__inputIcon" aria-hidden="true" />
                            <input
                                id="email"
                                type="email"
                                className="auth__input"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    {mode !== 'forgot' && (
                        <div className="auth__field">
                            <label className="auth__label" htmlFor="password">Password</label>
                            <div className="auth__inputWrapper">
                                <Lock className="auth__inputIcon" aria-hidden="true" />
                                <input
                                    id="password"
                                    type="password"
                                    className="auth__input"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={6}
                                />
                            </div>
                        </div>
                    )}

                    {mode === 'login' && (
                        <button
                            type="button"
                            className="auth__forgot"
                            onClick={() => { setMode('forgot'); setError(''); setMessage(''); }}
                        >
                            Forgot password?
                        </button>
                    )}

                    <button className="auth__submit" type="submit" disabled={loading}>
                        {loading ? (
                            <Loader2 className="auth__spinner" />
                        ) : (
                            <>
                                {mode === 'forgot' ? 'Send Reset Link' : mode === 'login' ? 'Sign In' : 'Create Account'}
                                <ArrowRight className="auth__submitIcon" aria-hidden="true" />
                            </>
                        )}
                    </button>
                </form>

                {mode !== 'forgot' && (
                    <p className="auth__footer">
                        {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
                        <button
                            className="auth__switch"
                            onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
                            type="button"
                        >
                            {mode === 'login' ? 'Sign up' : 'Sign in'}
                        </button>
                    </p>
                )}
            </div>
        </div>
    );
}
